import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';
import Select from 'react-select';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import Datatable styles (keep visual parity)
import "../../assets/scss/datatables.scss";

// Import API helper
import httprequester from '../../lib/httprequester';

// ---------- Lightweight configuration ----------
const LIST_API_ENDPOINT = 'webinar/cms/post-event/list';
const WEBINARS_DROPDOWN_ENDPOINT = 'webinar/cms/webinars-dropdown';
const ALLOWED_SORT_BY = ['duration', 'name', 'email', 'entered_at', 'exited_at', 'status'];
const ALLOWED_SORT_ORDER = ['asc', 'desc'];

// Map raw API list items into a normalized shape used by the table below.
// Adjust this to your API fields as needed.
const normalizeItem = (item, index) => {
  const id = item?.user_id ?? item?.id ?? item?.participant_id ?? `temp-${index}`;
  const name = item?.user_name ?? item?.name ?? '-';
  const email = item?.user_email ?? item?.email ?? '-';
  const nik = item?.nik ?? item?.NIK ?? '-';
  const sessions = item?.total_sessions ?? item?.sessions ?? 0;
  const durationSeconds = item?.total_duration_seconds ?? item?.duration ?? item?.total_duration ?? 0;
  const firstEnteredAt = item?.first_entered_at ?? item?.firstEnteredAt ?? null;
  const lastExitedAt = item?.last_exited_at ?? item?.lastExitedAt ?? null;
  const webinarTitle = item?.webinar_title ?? item?.title ?? '-';
  const files = item?.files ?? [];

  return {
    ...item,
    _id: id,
    _name: name,
    _email: email,
    _nik: nik,
    _sessions: sessions,
    _duration: durationSeconds,
    _first_entered_at: firstEnteredAt,
    _last_exited_at: lastExitedAt,
    _webinar_title: webinarTitle,
    files: files,
    uniqueKey: `${id}-${Date.now()}-${index}`
  };
};

// ---------- Helpers ----------
// Status rank used for sorting: Pending(0) < Approved(1) < Rejected(2) < No Files(3)
const getStatusRank = (row) => {
  const files = row?.files || [];
  if (files.length === 0) return 3;
  const val = files[0]?.is_approved;
  if (val === 1 || val === '1' || val === true) return 1;
  if (val === 0 || val === '0' || val === false) return 2;
  return 0;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('id-ID', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Returns a real JS Date (not a formatted string) so Excel/Google Sheets
// stores it as a native date/time value that can be sorted and used in formulas.
const toExcelDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return Number.isNaN(d.getTime()) ? '' : d;
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '-';
  const total = Math.max(0, Math.floor(Number(seconds)));
  if (!Number.isFinite(total)) return '-';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const StatusBadge = ({ value }) => {
  const compute = (val) => {
    if (val === null || val === undefined) return { text: 'PENDING', cls: 'bg-warning badge-warning text-dark' };
    if (val === 1) return { text: 'APPROVED', cls: 'bg-success badge-success' };
    if (val === 0) return { text: 'REJECTED', cls: 'bg-danger badge-danger' };
    return { text: String(val).toUpperCase(), cls: 'bg-secondary badge-secondary' };
  };

  const { text, cls } = useMemo(() => compute(value), [value]);
  return (
    <span className={`badge ${cls}`}>{text}</span>
  );
};

StatusBadge.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const PostEvent = () => {
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    webinar_id: '',
    search: '',
    page: 1,
    limit: 10,
    sort_by: 'duration',
    sort_order: 'desc'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10
  });
  const [webinarOptions, setWebinarOptions] = useState([]);
  const [webinarLoading, setWebinarLoading] = useState(false);
  const [summary, setSummary] = useState({
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    averageDuration: 0
  });

  // File preview modal state
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalUrl, setFileModalUrl] = useState('');
  const [fileModalIsPdf, setFileModalIsPdf] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(false);

  const [pageInput, setPageInput] = useState('1');

  const clearAlert = () => setError('');
  const clearSuccess = () => setSuccess('');

  // Fetch webinar options
  const fetchWebinarOptions = useCallback(async () => {
    try {
      setWebinarLoading(true);
      setError('');
      const response = await httprequester.getAsync(WEBINARS_DROPDOWN_ENDPOINT);
      const arr = response?.data?.data;
      if (Array.isArray(arr)) {
        const formatted = arr.map(w => ({ webinar_id: w.webinar_id, title: w.title, date: w.date, status: w.status }));
        setWebinarOptions(formatted);
      } else {
        setWebinarOptions([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching webinar options:', e);
      setWebinarOptions([]);
      setError('Failed to load webinars');
    } finally {
      setWebinarLoading(false);
    }
  }, []);

  // 'all' is sent as a very large limit since the backend expects a number
  const resolveLimit = (limit) => (limit === 'all' ? '100000' : String(limit || 10));

  // Fetch list from backend
  const fetchList = useCallback(async (overrideFilters) => {
    const f = overrideFilters || filters;
    setLoading(true);
    setError('');

    try {
      const sortBy = ALLOWED_SORT_BY.includes(f.sort_by) ? f.sort_by : 'duration';
      const sortOrder = ALLOWED_SORT_ORDER.includes(String(f.sort_order).toLowerCase()) ? String(f.sort_order).toLowerCase() : 'desc';
      const query = new URLSearchParams({
        page: String(f.page || 1),
        limit: resolveLimit(f.limit),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (f.webinar_id) query.append('webinar_id', String(f.webinar_id));
      if (f.search) query.append('search', f.search.trim());

      const response = await httprequester.getAsync(`${LIST_API_ENDPOINT}?${query.toString()}`);

      // Response shape:
      // { message, data: { data: [...], page, perPage, total, lastPage } }
      const outer = response?.data;
      const container = outer?.data ?? {};

      let list = [];
      let nextPagination = { ...pagination };

      const dataArray = Array.isArray(container?.data) ? container.data : [];
      list = dataArray;
      nextPagination = {
        current_page: parseInt(container?.page) || 1,
        total_pages: parseInt(container?.lastPage) || 1,
        total_items: parseInt(container?.total) || dataArray.length || 0,
        items_per_page: parseInt(container?.perPage) || (f.limit === 'all' ? (dataArray.length || 1) : (f.limit || 10))
      };

      const normalized = list.map((item, index) => normalizeItem(item, index));
      if (sortBy === 'status') {
        // Stable sort within the page; the backend is expected to order across pages.
        const dir = sortOrder === 'desc' ? -1 : 1;
        normalized.sort((a, b) => dir * (getStatusRank(a) - getStatusRank(b)));
      }
      setItems(normalized);
      setPagination(nextPagination);

      // Prefer backend-provided summary when available
      const backendSummary = container.summary || container.counts;
      if (backendSummary) {
        setSummary({
          pendingCount: Number(backendSummary.pendingCount) || 0,
          approvedCount: Number(backendSummary.approvedCount) || 0,
          rejectedCount: Number(backendSummary.rejectedCount) || 0,
          averageDuration: Number(backendSummary.averageDuration) || 0
        });
      } else {
        // Fallback to computing summary from current page only
        let pending = 0;
        let approved = 0;
        let rejected = 0;
        let totalDuration = 0;

        for (const item of normalized) {
          const files = item?.files || [];
          totalDuration += Math.max(0, Number(item?._duration) || 0);

          if (files.length === 0) {
            continue;
          }

          const latestFile = files[0];
          const val = latestFile?.is_approved;

          if (val === null || val === undefined) pending += 1;
          else if (val === 1 || val === '1' || val === true) approved += 1;
          else if (val === 0 || val === '0' || val === false) rejected += 1;
          else pending += 1;
        }

        const averageDuration = normalized.length ? Math.floor(totalDuration / normalized.length) : 0;
        setSummary({
          pendingCount: pending,
          approvedCount: approved,
          rejectedCount: rejected,
          averageDuration
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching list:', err);
      setError('Error fetching data');
      setItems([]);
      setPagination({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: f.limit === 'all' ? 1 : (f.limit || 10) });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  useEffect(() => {
    fetchWebinarOptions();
    fetchList();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key !== 'page') next.page = 1;
      if (key === 'page' || key === 'limit') fetchList(next);
      return next;
    });
  };

  // Keep the "go to page" input in sync with the actual current page
  useEffect(() => {
    setPageInput(String(pagination.current_page));
  }, [pagination.current_page]);

  const handleGoToPage = () => {
    const maxPage = pagination.total_pages || 1;
    let target = parseInt(pageInput, 10);
    if (Number.isNaN(target)) target = pagination.current_page;
    target = Math.min(Math.max(target, 1), maxPage);
    setPageInput(String(target));
    if (target !== pagination.current_page) {
      handleFilterChange('page', target);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchList();
  };

  const resetFilters = () => {
    setFilters({ webinar_id: '', search: '', page: 1, limit: 10, sort_by: 'duration', sort_order: 'desc' });
  };

  const handleExportExcel = async () => {
    try {
      const f = filters;
      const query = new URLSearchParams({
        page: '1',
        limit: resolveLimit(f.limit),
        sort_by: f.sort_by || 'duration',
        sort_order: f.sort_order || 'desc',
        export_all: '1'
      });
      if (f.webinar_id) query.append('webinar_id', String(f.webinar_id));
      if (f.search) query.append('search', f.search.trim());

      const response = await httprequester.getAsync(`${LIST_API_ENDPOINT}?${query.toString()}`);
      const outer = response?.data;
      const container = outer?.data ?? {};
      const rawRows = Array.isArray(container?.data) ? container.data : [];

      if (rawRows.length === 0) {
        setError('No data to export');
        return;
      }

      const rows = rawRows.map(normalizeItem);

      const data = rows.map(r => {
        return {
          'Full Name': r._name ?? '',
          'Email': r._email ?? '',
          'NIK': r._nik ?? '',
          'Specialist': r.specialist ?? '',
          'Phone': r.user_handphone ?? '',
          'Registered At': toExcelDate(r.created_at),
          'Join Time': toExcelDate(r._first_entered_at),
          'Exit time': toExcelDate(r._last_exited_at),
          'Duration': formatDuration(r._duration)
        };
      });

      const mod = await import('xlsx');
      const XLSX = mod.default || mod;
      // ISO 8601 order (yyyy-mm-dd) avoids the DD/MM vs MM/DD ambiguity that
      // makes Excel/Sheets misparse or reject dates depending on the opener's locale.
      const worksheet = XLSX.utils.json_to_sheet(data, {
        cellDates: true,
        dateNF: 'yyyy-mm-dd hh:mm:ss'
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Post Event');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `post-event-${ts}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Export error:', e);
      setError('Failed to export to Excel. Please install dependency: npm i xlsx');
    }
  };

  const webinarSelectOptions = [
    { value: '', label: 'Select webinar...' },
    ...webinarOptions.map(w => ({ value: w.webinar_id, label: w.title }))
  ];

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': { border: '1px solid #80bdff' },
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      border: '1px solid #ced4da',
      borderRadius: '0.375rem'
    })
  };

  // ---------- File helpers & modal handlers ----------
  const ensureHttps = (url) => {
    if (!url) return '';
    const u = String(url);
    return u.startsWith('http:') ? `https:${u.slice(5)}` : u;
  };

  const isPdfUrl = (url) => {
    if (!url) return false;
    try {
      const u = new URL(url, window.location.origin);
      return /\.pdf($|\?)/i.test(u.pathname);
    } catch (e) {
      return /\.pdf($|\?)/i.test(String(url));
    }
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    try {
      const u = new URL(url, window.location.origin);
      return /\.(png|jpg|jpeg|gif|webp|bmp|svg)($|\?)/i.test(u.pathname);
    } catch (e) {
      return /\.(png|jpg|jpeg|gif|webp|bmp|svg)($|\?)/i.test(String(url));
    }
  };

  const openFileModal = (row) => {
    setSelectedRow(row || null);
    setSelectedFile(null);
    setFileModalUrl('');
    setFileModalIsPdf(false);
    setFileModalOpen(true);
  };

  const closeFileModal = () => {
    setFileModalOpen(false);
    setSelectedRow(null);
    setSelectedFile(null);
    setFileModalUrl('');
    setFileModalIsPdf(false);
  };

  const handleFileClick = (file) => {
    const finalUrl = ensureHttps(file.file_upload);
    setSelectedFile(file);
    setFileModalUrl(finalUrl);
    setFileModalIsPdf(isPdfUrl(finalUrl) && !isImageUrl(finalUrl));
  };

  const handleApproveFile = () => {
    setConfirmAction('approve');
  };

  const handleRejectFile = () => {
    setConfirmAction('reject');
  };

  const submitFileAction = async () => {
    if (submittingAction) return;
    setSubmittingAction(true);
    const file = selectedFile;
    try {
      if (!file) throw new Error('No file selected');
      const fileId = file.file_id;

      const actionValue = confirmAction === 'approve' ? 'approve' : 'reject';
      await httprequester.postAsync('webinar/cms/post-event/file-upload/approve', {
        file_id: fileId,
        action: actionValue
      });

      setSuccess(confirmAction === 'approve' ? 'File approved successfully' : 'File rejected successfully');
      fetchList();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('File action error:', e);
      setError(confirmAction === 'approve' ? 'Failed to approve file' : 'Failed to reject file');
    } finally {
      setSubmittingAction(false);
      setConfirmAction(null);
      closeFileModal();
    }
  };

  // Columns definition (adjusted to current data)
  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '220px' }} title={row._name}>
            {row._name}
          </div>
          <small className="text-muted d-block">{row._email}</small>
        </div>
      )
    },
    {
      key: 'webinar',
      label: 'Webinar',
      render: (row) => (
        <div className="text-truncate" style={{ maxWidth: '320px' }} title={row._webinar_title}>
          {row._webinar_title}
        </div>
      )
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (row) => formatDuration(row._duration)
    },
    {
      key: 'created_at',
      label: 'First Entered',
      render: (row) => formatDate(row._first_entered_at)
    },
    {
      key: 'last_exited_at',
      label: 'Last Exited',
      render: (row) => formatDate(row._last_exited_at)
    },
    {
      key: 'approval_status',
      label: 'Status',
      render: (row) => {
        const files = row?.files || [];
        if (files.length === 0) {
          return <span className="badge bg-secondary">No Files</span>;
        }

        // Get the status of the latest file (most recent upload)
        const latestFile = files[0]; // Files are ordered by created_at desc
        return <StatusBadge value={latestFile.is_approved} />;
      }
    },
    {
      key: 'certificate',
      label: 'Certificate',
      render: (row) => {
        const files = row?.files || [];
        const hasFiles = files.length > 0;

        if (!hasFiles) {
          return <span className="text-muted">-</span>;
        }

        // Count approvals
        const approvedCount = files.filter(f => f.is_approved === 1 || f.is_approved === '1' || f.is_approved === true).length;
        const rejectedCount = files.filter(f => f.is_approved === 0 || f.is_approved === '0' || f.is_approved === false).length;
        const pendingCount = files.filter(f => f.is_approved === null || f.is_approved === undefined).length;

        return (
          <div className="d-flex align-items-center gap-2">
            <Button color="info" size="sm" onClick={() => openFileModal(row)}>
              <i className="bx bx-file me-1"></i>
              {files.length} {files.length === 1 ? 'file' : 'files'}
            </Button>
            {pendingCount > 0 && (
              <span className="badge bg-warning">{pendingCount} pending</span>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Post Event | Ruang Ilmiah CMS</title>
        </MetaTags>

        <div className="container-fluid">
          <Breadcrumbs title="Post Event" breadcrumbItem="CME Online" />

          {/* Filters Section */}
          <Card className="mb-4">
            <CardBody>
              <form onSubmit={handleSearch}>
                <Row>
                  <Col md="3">
                    <label className="form-label">Webinar</label>
                    <div style={{ position: 'relative', marginBottom: 0 }}>
                      <Select
                        isClearable
                        isSearchable
                        placeholder={webinarLoading ? 'Loading webinars...' : 'Select webinar...'}
                        options={webinarSelectOptions}
                        value={webinarSelectOptions.find(opt => opt.value === (filters.webinar_id ? parseInt(filters.webinar_id) : '')) || webinarSelectOptions[0]}
                        onChange={(opt) => setFilters(prev => ({ ...prev, webinar_id: opt ? opt.value : '' }))}
                        styles={selectStyles}
                        isLoading={webinarLoading}
                        isDisabled={webinarLoading}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => webinarOptions.length === 0 ? 'No webinars available' : 'No webinars found'}
                      />
                    </div>
                  </Col>
                  <Col md="2">
                    <label className="form-label">Search</label>
                    <Input
                      type="text"
                      placeholder="Search by name or email..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      style={{ minHeight: '38px' }}
                    />
                  </Col>
                  <Col md="2">
                    <label className="form-label">Sort By</label>
                    <Input
                      type="select"
                      value={filters.sort_by}
                      onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                      style={{ minHeight: '38px' }}
                    >
                      <option value="duration">Duration</option>
                      <option value="name">Name</option>
                      <option value="email">Email</option>
                      <option value="entered_at">First Entered</option>
                      <option value="exited_at">Last Exited</option>
                      <option value="status">Status (Pending, Approved, Rejected, No Files)</option>
                    </Input>
                  </Col>
                  <Col md="2">
                    <label className="form-label">Order</label>
                    <Input
                      type="select"
                      value={filters.sort_order}
                      onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                      style={{ minHeight: '38px' }}
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </Input>
                  </Col>
                  <Col md="3" className="d-flex align-items-end">
                    <div className="d-flex gap-2">
                      <Button color="primary" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : 'Search'}
                      </Button>
                      <Button color="secondary" type="button" onClick={resetFilters}>
                        Reset
                      </Button>
                      <Button color="success" type="button" onClick={handleExportExcel} disabled={loading || items.length === 0}>
                        <i className="bx bx-download me-1"></i>
                        Export
                      </Button>
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>

          {/* Summary */}
          <Row>
            <Col md="3">
              <Card className="text-center">
                <CardBody>
                  <div className="text-warning">
                    <i className="bx bx-time-five bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summary.pendingCount}</h4>
                  <p className="text-muted mb-0">Pending</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="text-center">
                <CardBody>
                  <div className="text-success">
                    <i className="bx bx-check-circle bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summary.approvedCount}</h4>
                  <p className="text-muted mb-0">Approved</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="text-center">
                <CardBody>
                  <div className="text-danger">
                    <i className="bx bx-x-circle bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summary.rejectedCount}</h4>
                  <p className="text-muted mb-0">Rejected</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="text-center">
                <CardBody>
                  <div className="text-primary">
                    <i className="bx bx-timer bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{formatDuration(summary.averageDuration)}</h4>
                  <p className="text-muted mb-0">Avg Watch Time</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Results Section */}
          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading data...</p>
                </div>
              ) : error ? (
                <Alert color="danger" isOpen={!!error} toggle={clearAlert}>
                  {error}
                </Alert>
              ) : items.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bx bx-file bx-lg text-muted"></i>
                  <p className="mt-2">No data found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        {columns.map((column) => (
                          <th key={column.key}>
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr
                          style={{ cursor: 'auto' }}
                          key={row.uniqueKey || row._id}
                        >
                          {columns.map((column) => (
                            <td key={`${row.uniqueKey || row._id}-${column.key}`}>
                              {column.render ? column.render(row) : row[column.key]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Pagination */}
          {pagination.total_items > 0 && (
            <Row className="mt-3 mb-4">
              <Col className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span>
                    Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of{' '}
                    {pagination.total_items} results
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0 text-nowrap">Rows per page</label>
                    <Input
                      type="select"
                      style={{ width: '90px', minHeight: '38px' }}
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', e.target.value === 'all' ? 'all' : Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">All</option>
                    </Input>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    color="outline-primary"
                    disabled={pagination.current_page === 1}
                    onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                  >
                    Previous
                  </Button>
                  <div className="d-flex align-items-center gap-1">
                    <span>Page</span>
                    <Input
                      type="number"
                      min={1}
                      max={pagination.total_pages}
                      style={{ width: '70px', minHeight: '38px' }}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleGoToPage(); }}
                    />
                    <span>of {pagination.total_pages}</span>
                    <Button color="outline-secondary" onClick={handleGoToPage}>
                      Go
                    </Button>
                  </div>
                  <Button
                    color="outline-primary"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Success Alert */}
      {success && (
        <SweetAlert
          success
          title="Success!"
          onConfirm={clearSuccess}
        >
          {success}
        </SweetAlert>
      )}

      {/* Confirm Approve/Reject */}
      {confirmAction && (
        <SweetAlert
          warning
          showCancel={!submittingAction}
          title={submittingAction
            ? (confirmAction === 'approve' ? 'Approving...' : 'Rejecting...')
            : (confirmAction === 'approve' ? 'Approve this file?' : 'Reject this file?')}
          confirmBtnText={confirmAction === 'approve' ? 'Yes, approve' : 'Yes, reject'}
          cancelBtnText="Cancel"
          onConfirm={submitFileAction}
          onCancel={() => setConfirmAction(null)}
          showConfirm={!submittingAction}
        >
          {submittingAction
            ? 'Please wait while we process your request...'
            : (confirmAction === 'approve'
              ? 'This will approve the certificate file.'
              : 'This will reject the certificate file.')}
        </SweetAlert>
      )}

      {/* File Preview Modal */}
      <Modal isOpen={fileModalOpen} toggle={closeFileModal} size="xl" centered>
        <ModalHeader toggle={closeFileModal}>
          Certificate Files - {selectedRow?._name || selectedRow?.user_name || 'User'}
        </ModalHeader>
        <ModalBody>
          <div className="row">
            {/* File List */}
            <div className="col-md-4">
              <h6 className="mb-3">Uploaded Files ({selectedRow?.files?.length || 0})</h6>
              {selectedRow?.files && selectedRow.files.length > 0 ? (
                <div className="list-group" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {selectedRow.files.map((file, index) => (
                    <div
                      key={file.file_id || index}
                      className={`list-group-item list-group-item-action ${selectedFile?.file_id === file.file_id ? 'active' : ''
                        }`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleFileClick(file)}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <small>
                          <i className="bx bx-file me-1"></i>
                          File #{index + 1}
                        </small>
                        <StatusBadge value={file.is_approved} />
                      </div>
                      <small className="d-block text-muted mt-1">
                        {file.created_at ? new Date(file.created_at).toLocaleDateString('id-ID') : ''}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No files uploaded</p>
              )}
            </div>

            {/* File Preview */}
            <div className="col-md-8">
              <h6 className="mb-3">Preview</h6>
              {fileModalUrl ? (
                fileModalIsPdf ? (
                  <div style={{ height: '60vh', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                    <iframe
                      src={fileModalUrl}
                      title="Certificate PDF"
                      width="100%"
                      height="100%"
                      style={{ border: 0, borderRadius: '0.375rem' }}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={fileModalUrl}
                      alt="Certificate"
                      style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: '0.375rem' }}
                    />
                  </div>
                )
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="bx bx-file bx-lg"></i>
                  <p className="mt-2">Select a file to preview</p>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {selectedFile ? (
            selectedFile.is_approved === null || selectedFile.is_approved === undefined ? (
              <>
                <Button
                  color="success"
                  onClick={handleApproveFile}
                  disabled={submittingAction}
                >
                  Approve Selected File
                </Button>
                <Button
                  color="danger"
                  onClick={handleRejectFile}
                  disabled={submittingAction}
                >
                  Reject Selected File
                </Button>
                <Button color="secondary" onClick={closeFileModal}>Close</Button>
              </>
            ) : (
              <div className="d-flex align-items-center w-100">
                <StatusBadge value={selectedFile.is_approved} />
                <span className="text-muted ms-2">
                  This file has been {selectedFile.is_approved === 1 ? 'approved' : 'rejected'}
                </span>
                <div className="ms-auto">
                  <Button color="secondary" onClick={closeFileModal}>Close</Button>
                </div>
              </div>
            )
          ) : (
            <Button color="secondary" onClick={closeFileModal}>Close</Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PostEvent;


